import { useEffect, useMemo, useState, type ChangeEvent } from 'react'
import {
  api,
  type Block,
  type GoldenCase as CaseRow,
  type GoldenLabel,
  type LabelRegion,
  type SimulationRun,
} from '../lib/api.ts'
import { PrescriptionViewer } from '../components/PrescriptionViewer.tsx'
import { PreparationEditor } from '../components/PreparationEditor.tsx'
import { ProgressTrack } from '../components/ProgressTrack.tsx'
import { Button } from '../components/Button.tsx'
import { CaseStatus } from '../components/CaseStatus.tsx'
import { GoldenCaseRun } from '../components/GoldenCaseRun.tsx'
import { RegionRows } from '../components/RegionRows.tsx'
import { Input } from '../components/Input.tsx'
import { Select } from '../components/Select.tsx'
import { icons } from '../components/icons.ts'

/**
 * One golden-set case: the page beside what it is supposed to read as.
 *
 * A label is the answer key the reader is measured against, and checking one
 * meant opening a JSON file next to an image and holding both in your head. A
 * wrong region is invisible in JSON and obvious the moment it is drawn.
 *
 * **Walked in steps, like everything else here.** The document is step 0 and
 * each preparation is a step after it -- the same shape as the conference and
 * the simulation, because it is the same job: one part of one document at a
 * time, with the page beside it.
 *
 * **Edited here, applied as a diff.** Corrections live in this page's status
 * and leave through *Copiar* or *Baixar*. The screen deliberately cannot
 * write: labels are reviewed like code, and a second writer that bypasses the
 * diff is how a target quietly stops matching what anybody agreed to.
 */

/** Addressing a labelled value, matching the paths the boxes tool uses. */
type FieldPath = string

interface Located {
  readonly path: FieldPath
  readonly refs: readonly string[]
}

/** The labelled regions, as blocks the viewer already knows how to draw. */
/*
 * A cleared region is not drawn.
 *
 * Clearing zeroes a row rather than deleting it, so "no box" and "a box of no
 * size" have to mean the same thing everywhere -- otherwise the page carries
 * an invisible rectangle that the eye can still select and the scorer still
 * counts as a citation.
 */
const drawable = ({ region }: { region: LabelRegion }): boolean => region.w > 0 && region.h > 0

function regionBlocks(label: GoldenLabel): Block[] {
  const toBlock = ({
    path,
    region,
    i,
  }: {
    path: string
    region: LabelRegion
    i: number
  }): Block => ({
    block_ref: `${path}#${i}`,
    page: region.page,
    text: path,
    x: String(region.x),
    y: String(region.y),
    w: String(region.w),
    h: String(region.h),
  })

  // The two document-level names, boxed like every other field now that they
  // hold values rather than judgements.
  const names = (['patient', 'prescriber', 'prescriberRegistration'] as const).flatMap((who) =>
    (label[who]?.regions ?? [])
      .map((region, i) => ({ path: who, region, i }))
      .filter(drawable)
      .map(toBlock),
  )

  return [
    ...names,
    ...label.preparations.flatMap((preparation, f) => {
      const own = ([`form`, `posology`, `quantity`] as const).flatMap((field) =>
        preparation[field].regions.map((region, i) => ({ path: `f${f}.${field}`, region, i })),
      )
      const ingredients = preparation.ingredients.flatMap((ingredient, c) => [
        ...ingredient.name.regions.map((region, i) => ({ path: `f${f}.c${c}.name`, region, i })),
        ...ingredient.dose.regions.map((region, i) => ({ path: `f${f}.c${c}.dose`, region, i })),
      ])
      return [...own, ...ingredients].map(toBlock)
    }),
  ]
}

export interface GoldenCaseProps {
  readonly caseId: string
  readonly onOpenCase: (caseId: string) => void
  readonly onBack: () => void
  /** Where a simulation started from this case is followed. */
  readonly onSimulate: (uploadId: string) => void
  /** Where an verification a past run produced is opened. */
  readonly onOpenVerification: (verificationId: string) => void
}

export function GoldenCase({
  caseId,
  onOpenCase,
  onBack,
  onSimulate,
  onOpenVerification,
}: GoldenCaseProps) {
  const [cases, setCases] = useState<CaseRow[]>([])
  const [label, setLabel] = useState<GoldenLabel | undefined>()
  const [pageUrls, setPageUrls] = useState<string[]>([])
  const [step, setStep] = useState(0)
  const [visited, setVisited] = useState<ReadonlySet<number>>(new Set([0]))
  const [located, setLocated] = useState<Located | undefined>()
  const [copied, setCopied] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [simulating, setSimulating] = useState(false)
  const [running, setRunning] = useState(false)
  const [history, setHistory] = useState<
    { runs: SimulationRun[]; labelIsDraft: boolean } | undefined
  >()
  const [error, setError] = useState<string | undefined>()

  useEffect(() => {
    void api
      .goldenSet()
      .then((r) => setCases(r.cases))
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    setLabel(undefined)
    setDirty(false)
    setLocated(undefined)
    setStep(0)
    setVisited(new Set([0]))
    void api
      .goldenCase(caseId)
      .then((r) => {
        setLabel(r.label)
        setError(undefined)
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar o caso'),
      )
  }, [caseId])

  useEffect(() => {
    if (label === undefined) return undefined
    let live = true
    const created: string[] = []
    void Promise.all(
      Array.from({ length: label.pageCount }, (_, i) => api.goldenPageBlob(caseId, i)),
    ).then((urls) => {
      const usable = urls.filter((u): u is string => u !== undefined)
      created.push(...usable)
      if (live) setPageUrls(usable)
      else usable.forEach((u) => URL.revokeObjectURL(u))
    })
    return () => {
      live = false
      created.forEach((u) => URL.revokeObjectURL(u))
    }
  }, [caseId, label])

  const blocks = useMemo(() => (label === undefined ? [] : regionBlocks(label)), [label])

  /** Highlights one field's boxes, or every box when nothing is chosen. */
  function locate(path: FieldPath) {
    const refs = blocks.filter((b) => b.block_ref.startsWith(`${path}#`)).map((b) => b.block_ref)
    // A field with no region cannot be shown, and pretending otherwise would
    // clear the page for no reason. Keeping the current frame says more.
    if (refs.length === 0) return
    setLocated((current) => (current?.path === path ? undefined : { path, refs }))
  }

  /**
   * The regions of whatever `path` names.
   *
   * One accessor for both directions, so a coordinate typed into a row, a box
   * dragged on the page, and the eye toggle all move the same array. The
   * alternative was a switch per caller, which is where the two ends drift
   * apart and one of them starts writing to a copy.
   */
  function regionsAt(draft: GoldenLabel, path: string): LabelRegion[] | undefined {
    if (path === 'patient' || path === 'prescriber') return draft[path].regions
    if (path === 'prescriberRegistration') return draft.prescriberRegistration?.regions
    const m = /^f(\d+)\.(?:c(\d+)\.)?(name|dose|form|posology|quantity)$/.exec(path)
    if (m === null) return undefined
    const preparation = draft.preparations[Number(m[1])]
    if (preparation === undefined) return undefined
    if (m[2] === undefined) {
      const field = m[3] as 'form' | 'posology' | 'quantity'
      return preparation[field].regions
    }
    const ingredient = preparation.ingredients[Number(m[2])]
    if (ingredient === undefined) return undefined
    return ingredient[m[3] as 'name' | 'dose'].regions
  }

  /*
   * A field nobody has filled in yet.
   *
   * Two of them, because the schema distinguishes: an ingredient always has a
   * name, even an empty one, while a dose or a posology may legitimately not
   * exist -- a finished product carries no dose, and null says so. An empty
   * string there would claim the prescription says "", which the scorer would
   * then try to match.
   */
  const blank = () => ({ value: null, regions: [] })
  const blankName = () => ({ value: '', regions: [] })

  function regionRowsFor(path: string) {
    return (
      <RegionRows
        path={path}
        regions={regionsOf(path)}
        shown={located?.path === path ? located.refs[0] : undefined}
        pageCount={label?.pageCount ?? 1}
        onToggle={(ref) => toggleRegion(path, ref)}
        onChange={(i, region) =>
          edit((draft) => {
            const regions = regionsAt(draft, path)
            if (regions === undefined) return
            /*
              The row shown for a field with no regions is a placeholder, and
              typing into it is how one comes to exist. Writing at its index
              would silently do nothing, which looks like an input that
              ignores you.
            */
            if (i >= regions.length) regions.push(region)
            else regions[i] = region
          })
        }
      />
    )
  }

  async function openHistory() {
    setHistory({ runs: [], labelIsDraft: false })
    try {
      setHistory(await api.simulationsForCase(caseId))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
      setHistory(undefined)
    }
  }

  /** Sends the case's page through the ordinary simulation upload, then follows it. */
  async function simulate() {
    setSimulating(true)
    try {
      const { id } = await api.simulateGoldenCase(caseId)
      onSimulate(id)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setSimulating(false)
    }
  }

  /** Show one region alone, or return to showing every box. */
  function toggleRegion(path: string, ref: string) {
    setLocated((current) =>
      current?.refs[0] === ref && current.refs.length === 1 ? undefined : { path, refs: [ref] },
    )
  }

  const regionsOf = (path: string): LabelRegion[] =>
    label === undefined ? [] : (regionsAt(label, path) ?? [])

  function edit(change: (draft: GoldenLabel) => void) {
    setLabel((current) => {
      if (current === undefined) return current
      const draft = structuredClone(current) as GoldenLabel
      change(draft)
      return draft
    })
    setCopied(false)
    setDirty(true)
  }

  /**
   * Writes the corrected label back.
   *
   * The screen used to hand the JSON back for pasting, which made a correction
   * a two-step chore and meant a review session ended with a pile of files to
   * apply by hand. Saving in place keeps the label where it was -- still a diff
   * somebody decisions -- and removes the copying.
   */
  async function save() {
    if (label === undefined) return
    setSaving(true)
    try {
      await api.saveGoldenCase(label.caseId, label)
      setDirty(false)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setSaving(false)
    }
  }

  /**
   * Promotes or demotes the label, and writes it immediately.
   *
   * Immediately rather than marking the form dirty: this is not an edit to the
   * content, it is the decision that the content is fit to measure against,
   * and leaving it sitting unsaved beside "Salvar" would make the two look
   * like the same act.
   *
   * Until this existed there was no way to change the flag at all -- every
   * label sat at `draft`, and since evals skip drafts, the reader eval was
   * measuring against nothing.
   */
  async function setStatus(next: 'draft' | 'corrected') {
    if (label === undefined) return
    const updated = {
      ...label,
      status: next,
      correctedAt: next === 'corrected' ? new Date().toISOString().slice(0, 10) : null,
    }
    setSaving(true)
    try {
      await api.saveGoldenCase(updated.caseId, updated)
      setLabel(updated)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setSaving(false)
    }
  }

  function goToStep(next: number) {
    const clamped = Math.max(0, next)
    setVisited((seen) => new Set([...seen, step, clamped]))
    setStep(clamped)
    setLocated(undefined)
  }

  const index = Math.max(
    0,
    cases.findIndex((c) => c.caseId === caseId),
  )

  if (error !== undefined) {
    return (
      <div className="flex flex-col items-start gap-3 p-5">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Conjunto de referência
        </Button>
        <h1 className="text-tH4 text-text-strong">Não foi possível carregar o caso</h1>
        <p className="text-tP text-text-error">{error}</p>
      </div>
    )
  }

  if (label === undefined) {
    return (
      <p className="text-tP text-text-weak p-5" role="status" aria-live="polite">
        Carregando…
      </p>
    )
  }

  const lastStep = label.preparations.length
  const json = `${JSON.stringify(label, null, 2)}\n`

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-5 pt-0">
      {running && <GoldenCaseRun caseId={caseId} onClose={() => setRunning(false)} />}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Conjunto de referência
        </Button>
        <h1 className="text-tH4 text-text-strong">{label.caseId}</h1>
        <span className="text-tSmall text-text-weak">
          {index + 1} de {cases.length}
        </span>
        <span className="grow" />

        {/*
          What you do *to* this case, before what you do to move off it.
          
          Both were at the foot of the form, beside Salvar, where they read as
          part of editing the label -- and running the pipeline is not editing
          anything. In the header they sit with the case's identity, which is
          what they act on.
        */}
        <Button
          variant="secondary"
          tone="neutral"
          size="small"
          disabled={simulating}
          title="Enviar esta receita para o pipeline como simulação"
          onClick={() => void simulate()}
        >
          {simulating ? 'Enviando…' : 'Simular'}
        </Button>
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          title="Simulações já rodadas a partir deste caso"
          onClick={() => void openHistory()}
        >
          Simulações
        </Button>
        {/*
          The light question, beside the heavy one.

          *Simular* pushes the page through the whole pipeline and leaves a
          conferência behind; this reads the case and scores it against its own
          label, keeping nothing. Same button, same icon and same modal as the
          resolution set's, because it is the same act on the other answer key.
        */}
        <Button
          variant="secondary"
          tone="brand"
          size="small"
          leadingIcon={icons.goldenSet}
          title="Ler este caso de novo e comparar com o gabarito"
          onClick={() => setRunning(true)}
        >
          Rodar
        </Button>

        {/*
          Case to case. Arrows rather than words: the pair is a position in a
          list of nine, and two labelled buttons took more of the header than
          the case's own name.
        */}
        <Button
          variant="secondary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          aria-label="Caso anterior"
          title="Caso anterior"
          disabled={index === 0}
          onClick={() => {
            const prev = cases[index - 1]
            if (prev !== undefined) onOpenCase(prev.caseId)
          }}
        />
        <Button
          variant="secondary"
          tone="neutral"
          size="small"
          leadingIcon={icons.forward}
          aria-label="Próximo caso"
          title="Próximo caso"
          disabled={index >= cases.length - 1}
          onClick={() => {
            const next = cases[index + 1]
            if (next !== undefined) onOpenCase(next.caseId)
          }}
        />
      </div>

      <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto lg:grid-cols-2 lg:grid-rows-[minmax(0,1fr)] lg:overflow-visible">
        <section className="rounded-base border-stroke-weak bg-background-raised flex min-h-0 flex-col overflow-hidden border p-4">
          <PrescriptionViewer
            fill
            // A golden case's pages are dense and in order, so a page's number
            // is its position -- unlike an verification's, where the number is the
            // attachment's index and receipts have been left out.
            pageUrls={pageUrls.map((url, page) => ({ url, page }))}
            blocks={blocks}
            // Every box until a field is chosen, then only that field's. A
            // page with eight rectangles on it is a picture of a label; one
            // rectangle is a question about a value.
            highlighted={located?.refs ?? blocks.map((b) => b.block_ref)}
            /*
              Dragging writes straight back to the label, so the numbers in the
              rows and the rectangle on the page are one value seen twice.
              The block ref carries the field path and the region's index --
              `f0.c1.dose#0` -- which is all it takes to find it again.
            */
            onRegionChange={(blockRef, next) => {
              const hash = blockRef.lastIndexOf('#')
              const path = blockRef.slice(0, hash)
              const index = Number(blockRef.slice(hash + 1))
              edit((draft) => {
                const regions = regionsAt(draft, path)
                const current = regions?.[index]
                if (regions === undefined || current === undefined) return
                regions[index] = { ...current, ...next }
              })
            }}
          />
        </section>

        <section className="rounded-base border-stroke-weak flex min-h-0 flex-col overflow-hidden border">
          <ProgressTrack
            steps={[
              { id: 'doc', title: 'Documento', tone: 'clean' as const },
              ...label.preparations.map((preparation, i) => ({
                id: `f${i}`,
                title: preparation.form.value ?? `Preparação ${i + 1}`,
                tone: 'clean' as const,
              })),
            ]}
            index={step}
            visited={visited}
            onJump={goToStep}
            counterText={
              step === 0 ? 'Documento' : `Preparação ${step} de ${label.preparations.length}`
            }
            ariaLabel="Partes do caso"
          />

          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
            {step === 0 ? (
              <article className="rounded-base border-stroke-weak bg-background-raised flex flex-col gap-3 border p-4">
                <h3 className="text-tPSemi text-text-strong">Documento</h3>

                <Select
                  label="Escrita"
                  helperText="Nada na origem diz isto: uma pessoa decide, olhando a página."
                  value={label.writing}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    edit((draft) => {
                      draft.writing = e.target.value
                    })
                  }
                >
                  <option value="handwritten">Manuscrita</option>
                  <option value="printed">Impressa</option>
                  <option value="mixed">Mista</option>
                </Select>

                {/*
                  The names as written, boxed like any other field.

                  These were judgements -- confere / diverge -- so that a label
                  carried no patient data and could be tracked. That made both
                  fields unmeasurable: agreement says two documents matched,
                  never that the reader read the name correctly. Labels are no
                  longer tracked, so the names can be what they are.
                */}
                {(['patient', 'prescriber'] as const).map((who) => (
                  <div key={who} className="flex flex-col gap-0.5">
                    <Input
                      label={who === 'patient' ? 'Paciente' : 'Prescritor'}
                      helperText="Como está escrito na receita. Deixe vazio se não houver ou não der para ler."
                      value={label[who].value ?? ''}
                      onFocus={() => locate(who)}
                      onChange={(e) =>
                        edit((draft) => {
                          // Empty means absent or illegible, and that is a real
                          // answer -- not an empty string the scorer would then
                          // try to match against a name.
                          draft[who].value = e.target.value === '' ? null : e.target.value
                        })
                      }
                    />
                    {/* Directly beneath its own input, like every other field.
                        Collected under a heading of their own, the two rows sat
                        below both inputs with nothing saying which was which. */}
                    {regionRowsFor(who)}
                  </div>
                ))}

                {/* The registration (#277): its own field, since the reader
                    reports it apart from the name. Untouched, it stays
                    unlabelled and out of the score; cleared, it says the page
                    shows none. */}
                <div className="flex flex-col gap-0.5">
                  <Input
                    label="Registro profissional"
                    helperText="CRM, CRMV, CRN… como está na receita. Vazio se a receita não traz."
                    value={label.prescriberRegistration?.value ?? ''}
                    onFocus={() => locate('prescriberRegistration')}
                    onChange={(e) =>
                      edit((draft) => {
                        const value = e.target.value === '' ? null : e.target.value
                        if (draft.prescriberRegistration === undefined)
                          draft.prescriberRegistration = { value, regions: [] }
                        else draft.prescriberRegistration.value = value
                      })
                    }
                  />
                  {regionRowsFor('prescriberRegistration')}
                </div>

                <Input
                  label="Observações"
                  helperText="Sem dados de paciente ou prescritor."
                  value={label.notes}
                  onChange={(e) =>
                    edit((draft) => {
                      draft.notes = e.target.value
                    })
                  }
                />
              </article>
            ) : (
              <>
                <PreparationEditor
                  only={step - 1}
                  preparations={label.preparations.map((preparation) => ({
                    form: preparation.form.value,
                    posology: preparation.posology.value,
                    quantity: preparation.quantity.value,
                    ingredients: preparation.ingredients.map((ingredient) => ({
                      name: ingredient.name.value,
                      dose: ingredient.dose.value,
                    })),
                  }))}
                  onPreparationChange={(f, patch) =>
                    edit((draft) => {
                      const preparation = draft.preparations[f]
                      if (preparation === undefined) return
                      for (const key of ['form', 'posology', 'quantity'] as const) {
                        if (key in patch) preparation[key].value = patch[key] ?? null
                      }
                    })
                  }
                  onIngredientChange={(f, c, patch) =>
                    edit((draft) => {
                      const ingredient = draft.preparations[f]?.ingredients[c]
                      if (ingredient === undefined) return
                      if (patch.name !== undefined) ingredient.name.value = patch.name
                      if ('dose' in patch) ingredient.dose.value = patch.dose ?? null
                    })
                  }
                  onFocusField={locate}
                  onAddPreparation={() =>
                    edit((draft) => {
                      draft.preparations.push({
                        form: blank(),
                        posology: blank(),
                        quantity: blank(),
                        ingredients: [{ name: blankName(), dose: blank(), kind: 'active' }],
                      })
                    })
                  }
                  onRemovePreparation={(f) =>
                    edit((draft) => {
                      draft.preparations.splice(f, 1)
                    })
                  }
                  onAddIngredient={(f) =>
                    edit((draft) => {
                      draft.preparations[f]?.ingredients.push({
                        name: blankName(),
                        dose: blank(),
                        kind: 'active',
                      })
                    })
                  }
                  onRemoveIngredient={(f, c) =>
                    edit((draft) => {
                      draft.preparations[f]?.ingredients.splice(c, 1)
                    })
                  }
                  renderFieldExtra={(path) => regionRowsFor(path)}
                  /*
                    The per-field *ver* button is gone: every region now has
                    its own eye in the rows below, so a button that showed all
                    of a field's boxes at once was the coarser of two controls
                    for the same thing.
                  */
                />
                <p className="text-tSmall text-text-weak">
                  Clique num campo para destacar só ele na receita; clique de novo para ver todas as
                  regiões.
                </p>
              </>
            )}
          </div>

          <div className="border-stroke-weak bg-background-sunken flex shrink-0 flex-wrap items-center gap-2 border-t p-4">
            {/* The same pair as the case navigation above, because it is the
                same gesture one level down. */}
            <Button
              variant="secondary"
              tone="neutral"
              size="small"
              leadingIcon={icons.back}
              aria-label="Etapa anterior"
              title="Etapa anterior"
              disabled={step === 0}
              onClick={() => goToStep(step - 1)}
            />
            <Button
              variant="secondary"
              tone="neutral"
              size="small"
              leadingIcon={icons.forward}
              aria-label="Próxima etapa"
              title="Próxima etapa"
              disabled={step >= lastStep}
              onClick={() => goToStep(step + 1)}
            />

            <span className="grow" />

            {/*
              Escape hatches, beside Salvar rather than competing with it.

              Icons because they are the rarely-used pair: saving is the act
              this screen is for, and two labelled buttons next to it read as
              three equal choices. They still matter -- saving needs a route
              that only exists in development, and neither covers taking a
              label somewhere that is not this repository.
            */}
            <Button
              variant="tertiary"
              tone={copied ? 'brand' : 'neutral'}
              size="small"
              leadingIcon={icons.copy}
              aria-label="Copiar JSON"
              title={copied ? 'Copiado' : 'Copiar JSON'}
              onClick={() => {
                void navigator.clipboard.writeText(json).then(() => setCopied(true))
              }}
            />
            <Button
              variant="tertiary"
              tone="neutral"
              size="small"
              leadingIcon={icons.download}
              aria-label="Baixar JSON"
              title="Baixar JSON"
              onClick={() => {
                const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }))
                const link = document.createElement('a')
                link.href = url
                link.download = `${label.caseId}.json`
                link.click()
                URL.revokeObjectURL(url)
              }}
            />
            {/*
              Runs this case through the real pipeline.
              
              The label says what the page *should* read as; a simulation says
              what the readers actually make of it. Having both a click apart
              is the difference between a golden set that is a filing cabinet
              and one that is a test you can run.
              
              Unsaved edits are deliberately not undecided: what is simulated is
              the image, which no edit here changes.
            */}
            <Button
              variant="primary"
              tone="brand"
              size="small"
              disabled={saving || !dirty}
              onClick={() => void save()}
            >
              {saving ? 'Salvando…' : dirty ? 'Salvar' : 'Salvo'}
            </Button>
          </div>

          {/*
            The flag that decides whether this label is measured against at
            all, and the only place it can be changed.
          */}
          {label !== undefined && (
            <CaseStatus
              status={label.status}
              busy={saving}
              onChange={(next) => void setStatus(next)}
              {...(dirty
                ? { undecided: 'Salve as alterações antes de marcar como conferido.' }
                : {})}
            />
          )}
        </section>
      </div>

      {history !== undefined && (
        <SimulationHistory
          runs={history.runs}
          labelIsDraft={history.labelIsDraft}
          onOpen={(verificationId) => onOpenVerification(verificationId)}
          onClose={() => setHistory(undefined)}
        />
      )}
    </div>
  )
}

/**
 * What this case has been read as, over time.
 *
 * Newest first, because the question is almost always "did the last change
 * help" rather than "what did it do in July". A run that failed is listed too:
 * a reading that crashed is a result, and hiding it would make a run of
 * failures look like a run of nothing.
 */
function SimulationHistory({
  runs,
  labelIsDraft,
  onOpen,
  onClose,
}: {
  readonly runs: readonly SimulationRun[]
  readonly labelIsDraft: boolean
  /** Opens the verification a run produced -- what it concluded, not the form. */
  readonly onOpen: (verificationId: string) => void
  readonly onClose: () => void
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Simulações deste caso"
      onClick={onClose}
    >
      <div
        className="rounded-base bg-background-raised border-stroke-weak flex max-h-[80vh] w-full max-w-[40rem] flex-col gap-3 overflow-y-auto border p-4"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-tPSemi text-text-strong">Simulações deste caso</h2>
          <Button variant="tertiary" tone="neutral" size="small" onClick={onClose}>
            Fechar
          </Button>
        </div>

        {runs.length === 0 ? (
          <p className="text-tP text-text-weak">
            Nenhuma simulação ainda. Use <strong>Simular</strong> para rodar esta receita pelo
            pipeline.
          </p>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {['Quando', 'Situação', 'Pontuação', 'Ativos', 'Doses'].map((h) => (
                  <th
                    key={h}
                    scope="col"
                    className="border-stroke-weak text-tTiny text-text-weak border-b px-2 py-2 text-left font-semibold uppercase"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const openable = run.verificationId !== null
                return (
                  <tr
                    key={run.uploadId}
                    tabIndex={openable ? 0 : -1}
                    role={openable ? 'button' : undefined}
                    className={openable ? 'hover:bg-fill-weak/40 cursor-pointer' : undefined}
                    onClick={() => openable && onOpen(run.verificationId!)}
                    onKeyDown={(event) => {
                      if (!openable || (event.key !== 'Enter' && event.key !== ' ')) return
                      event.preventDefault()
                      onOpen(run.verificationId!)
                    }}
                  >
                    <td className="border-stroke-weak text-tP text-text-strong border-b px-2 py-2">
                      {new Date(run.createdAt).toLocaleString('pt-BR')}
                    </td>
                    <td className="border-stroke-weak text-tSmall border-b px-2 py-2">
                      <span
                        className={
                          run.failureReason === null ? 'text-text-weak' : 'text-text-error'
                        }
                      >
                        {run.failureReason !== null ? 'falhou' : (run.status ?? run.status)}
                      </span>
                    </td>
                    {/* The column the history exists for: read down it and you
                        see whether the last change helped. */}
                    <td className="border-stroke-weak text-tPSemi text-text-strong border-b px-2 py-2 tabular-nums">
                      {run.score === undefined ? '—' : `${Math.round(run.score.overall * 100)}%`}
                    </td>
                    <td className="border-stroke-weak text-tSmall text-text-weak border-b px-2 py-2 tabular-nums">
                      {run.score === undefined
                        ? '—'
                        : `${run.score.ingredients.correct}/${run.score.ingredients.expected}`}
                    </td>
                    <td className="border-stroke-weak text-tSmall text-text-weak border-b px-2 py-2 tabular-nums">
                      {run.score === undefined
                        ? '—'
                        : `${run.score.doses.correct}/${run.score.doses.expected}`}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {/*
          Said once, under the column, rather than on every row. A provisional
          number is still worth comparing against the row above it.
        */}
        {labelIsDraft && (
          <p className="text-tTiny text-text-weak">
            O gabarito ainda é rascunho — as pontuações são provisórias e não servem de baseline.
          </p>
        )}
      </div>
    </div>
  )
}
